import { Text } from '@react-pdf/renderer';
import { Button, Card, Col, Form, Input, message, Row, Select, Checkbox, Tooltip, Upload, Modal, Space, Radio } from 'antd'
import { ExclamationCircleOutlined, UploadOutlined, UserOutlined } from '@ant-design/icons';
import Title from 'antd/es/typography/Title';
import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import commonLookupService from 'src/services/commonLookupService';
import { recordError } from 'src/utils/logService';
import { CustomContactNumberInput } from '../custom/CustomContactNumberInput';
import { getProfile } from '@seims/shared/src/security';
import { util } from '@seims/shared';

// Receive onSave and initialValues props
export default function PersonalInformationForm({ onSave, onCancel, initialValues, readOnlyFields = [] }) {

  const [form] = Form.useForm();
  const otherLocation = Form.useWatch('otherLocation', form);
  const { Option } = Select;
  const { confirm } = Modal;
  const selectWidth = "50%";
  const formName = "personal_information";
  const navigate = useNavigate();
  // Function to convert image to base64 for preview
  const getBase64 = (img, callback) => {
    const reader = new FileReader();
    reader.addEventListener('load', () => callback(reader.result));
    reader.readAsDataURL(img);
  };
  const [loading, setLoading] = useState(false);
  const [imageUrl, setImageUrl] = useState();
  const [profilePictureFile, setProfilePictureFile] = useState(null);
  const [profilePicError, setProfilePicError] = useState(null);

  const [titles, setTitles] = useState([])
  const [gender, setGender] = useState([])
  const [race, setRace] = useState([]);
  const [disability, setDisability] = useState([]);
  const [languages, setLanguages] = useState([]);

  const [provinces, setProvinces] = useState([]);
  const [province, setProvince] = useState();
  const [district, setDistrict] = useState();
  const [districts, setDistricts] = useState([]);
  const [towns, setTowns] = useState([]);

  const uploadRef = useRef(null);
  const userInfo = getProfile();

  const [cameraModalVisible, setCameraModalVisible] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);
  const [preferredCommMethod, setPreferredCommMethod] = useState([]);


  const transformInitialValues = (values) => {

    form.resetFields(); // clears old state first
    if (!values) return {};
    const transformed = { ...values };
    // const imageUrl = profilePicture.base64 ? `data:${profilePicture.type};base64,${profilePicture.base64}` : null;
    if (transformed.profilePicture && typeof transformed.profilePicture.base64 === 'string' && transformed.profilePicture.base64) {
      // Construct the full Data URL for direct image display
      const fullDataUrl = `data:${transformed.profilePicture.type || 'image/png'};base64,${transformed.profilePicture.base64}`;

      setImageUrl(fullDataUrl); // Set for display in your <img> tag
      setProfilePictureFile(transformed.profilePicture)

      // Prepare the profilePicture array for Ant Design Upload component's initialFileList
      transformed.profilePicture = [{
        uid: transformed.profilePicture.uid || '-1', // Unique ID for Ant Design
        name: transformed.profilePicture.name || 'profile_picture.png', // Provide a fallback name
        status: 'done', // CRITICAL: Marks the file as successfully uploaded and validated by Ant Design
        url: fullDataUrl, // Use the full Data URL for the Upload component's preview as well
        size: transformed.profilePicture.size || 0, // Fallback size
        type: transformed.profilePicture.type || 'image/png' // Fallback type
      }];
    } else {
      setImageUrl(null); // Clear image if no initial picture
      transformed.profilePicture = []; // Ensure it's an empty array if no picture
    }

    if (transformed.dateOfBirth && typeof transformed.dateOfBirth === 'string') {
      transformed.dateOfBirth = dayjs(transformed.dateOfBirth).format("YYYY-MM-DD");
    }
    // Languages: Ensure it's an array of strings. It should already be in this format from parent.
    if (Array.isArray(transformed.languages)) {
      // Check if the items are objects (from API) and extract the 'value'
      if (typeof transformed.languages[0] === 'object' && transformed.languages[0] !== null && transformed.languages[0].value) {
        transformed.languages = transformed.languages.map(lang => lang.value).filter(Boolean);
      }
      // If they are already strings (names), the logic remains the same
      else {
        transformed.languages = transformed.languages.filter(Boolean);
      }
    } else {
      transformed.languages = [];
    }

    transformed.preferEmail = values.preferEmail || false;
    transformed.preferPhone = values.preferPhone || false;
    // Handle initial location values:
    if (transformed.location) {
      transformed.location = {
        province: transformed.location.province?.value || null,
        district: transformed.location.district?.value || null,
        town: transformed.location.town?.value || null,
      };
    }
    // Handle other lookup fields that come in as objects {key, value} and need to be string for form
    if (transformed.title && typeof transformed.title === 'object') { transformed.title = transformed.title.value; }
    if (transformed.race && typeof transformed.race === 'object') { transformed.race = transformed.race.value; }
    if (transformed.gender && typeof transformed.gender === 'object') { transformed.gender = transformed.gender.value; }
    if (transformed.disability && typeof transformed.disability === 'object') { transformed.disability = transformed.disability.value; }
    transformed.volunteerDifferentLocation = values.volunteerDifferentLocation ?? null;

    return transformed;
  };

  // Set initial form values using useEffect to handle prop changes
  useEffect(() => {
    loadLookups();
    if (initialValues) {
      const mappedValues = transformInitialValues(initialValues)
      form.setFieldsValue(mappedValues);
      const languagesArray = mappedValues.languages.map(obj => obj.value);
      const transformData = mappedValues.languages.map(obj => obj.key);
      setSelectedItems(transformData);

    }
    getUserDetails();
  }, [initialValues, form]);


  const loadLookups = async () => {
    getTitles();
    getGenders();
    getRaces();
    getDisability();
    getprovinceData();
    getLanguages();
  }

  const onChangeLanguage = (selectedValue) => {
    const selectedLanguages = languages.find(
      (c) => c.key === parseInt(selectedValue)
    );

    form.setFieldsValue({ scienceDomain: selectedLanguages });
    setSelectedItems(selectedLanguages);
  };

  const getUserDetails = () => {
    // Set only if initialValues from parent are not already populating
    // This prioritizes existing profile data over new user details for editing
    const currentFields = form.getFieldsValue();
    if (!currentFields.firstName && !currentFields.lastName && !currentFields.email) {
      form.setFieldsValue({
        firstName: userInfo.firstName,
        lastName: userInfo.lastName,
        email: userInfo.emailAddress,
      });
    }
  }

  const getTitles = async () => {
    try {
      const response = await commonLookupService.getTitles();
      if (response) {
        const { data } = response;
        setTitles(data);
      }
    } catch (error) {
      recordError(error);
    }
  };

  const getDisability = async () => {
    try {
      const response = await commonLookupService.getDisability();
      if (response) {
        const { data } = response;
        setDisability(data);
      }
    } catch (error) {
      recordError(error);
    }
  };

  const getGenders = async () => {
    try {
      const response = await commonLookupService.getGenders();
      if (response) {
        const { data } = response;
        setGender(data);
      }
    } catch (error) {
      recordError(error);
    }
  };

  const getRaces = async () => {
    try {
      const response = await commonLookupService.getRaces();
      if (response) {
        const { data } = response;
        setRace(data);
      }
    } catch (error) {
      recordError(error);
    }
  };

  const getprovinceData = async () => {
    try {
      const response = await commonLookupService.getProvincesDistrictTowns();
      if (response && response.status === 200) {
        const { data } = response;
        setProvinces(data);
      }
    } catch (error) {
      recordError(error);
    }
  };

  const sortValues = (data, label = '') => {
    const sortedValues = data.sort((a, b) => a.value.localeCompare(b.value));
    return sortedValues;
  };

  const filterActiveValues = (data, label = '') => {
    const _keyValues = data.flatMap((item) =>
      (item.active === true || item.active === undefined) ? { ...item } : []
    );

    return sortValues(_keyValues, label);
  }

  const getLanguages = async () => {
    try {
      const response = await commonLookupService.getLanguges();
      if (response && response.status === 200) {
        const { data } = response;
        const sorted = filterActiveValues(data, "language");
        const transformData = sorted.map((c) => ({
          label: c.value,
          value: c.value,
          key: c.key,
        }));
        setLanguages(transformData);
      } else {
        setLanguages([]);
      }
    } catch (error) {
      recordError(error);
    }
  };

  const handleProvinceChange = (selectedValue) => {
    setProvince(selectedValue);

    // Find the province object in your 'provinces' array by matching its 'value' property
    const foundProvince = provinces.find(prov => prov.value === selectedValue);

    if (foundProvince && foundProvince.districts) { // Check if province was found and has districts
      setDistricts(foundProvince.districts); // Set the districts for the found province
      // It's crucial to also clear the dependent fields (district and townCity) in the form
      form.setFieldsValue({ location: { district: undefined, town: undefined } });
    } else {
      console.warn("Could not find province or districts for:", selectedValue);
      setDistricts([]); // Clear districts if not found or no districts array
      form.setFieldsValue({ location: { district: undefined, town: undefined } });
    }
  };

  const handleDistrictChange = (selectedValue) => {
    setDistrict(selectedValue);

    const foundDistrict = districts.find(district => district.value === selectedValue);

    if (foundDistrict && foundDistrict.towns) { // Check if district was found and has towns
      setTowns(foundDistrict.towns); // Set the towns for the found district
      // It's crucial to also clear the dependent field (townCity) in the form
      form.setFieldsValue({ location: { town: undefined } });
    } else {
      console.warn("Could not find district or towns for:", selectedValue);
      setTowns([]); // Ensure towns are cleared if not found or no towns array
      form.setFieldsValue({ location: { town: undefined } });
    }
  };

  // New camera functions
  const startCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' } });
      setStream(mediaStream);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
    } catch (err) {
      console.error("Error accessing camera: ", err);
      message.error("Could not access camera. Please check your permissions.");
      setCameraModalVisible(false); // Close the modal on error
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
  };

  const handleTakePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const context = canvas.getContext('2d');
      context.drawImage(video, 0, 0, canvas.width, canvas.height);

      canvas.toBlob(blob => {
        const imageFile = new File([blob], "selfie.png", { type: "image/png" });

        getBase64(imageFile, url => {
          setImageUrl(url);

          const fileItem = {
            uid: Date.now().toString(),
            name: "selfie.png",
            status: "done",
            url,
            originFileObj: imageFile, // ✅ real File
          };

          form.setFieldsValue({ profilePicture: [fileItem] });
        });

        stopCamera();
        setCameraModalVisible(false);
      }, "image/png");

    }
  };

  const handleTakeSelfieClick = () => {
    setCameraModalVisible(true);
    startCamera();
  };

  const customRequest = ({ file, onSuccess, onError }) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const base64 = reader.result;
      setImageUrl(base64);

      const fileItem = {
        uid: file.uid,
        name: file.name,
        status: "done",
        url: base64,
        originFileObj: file, // ✅ keep the real File here
      };

      // update form
      form.setFieldsValue({ profilePicture: [fileItem] });

      onSuccess(fileItem);
    };
    reader.onerror = (error) => onError(error);
  };

  const beforeImageUpload = useCallback((file) => {
    const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png';
    if (!isJpgOrPng) {
      message.error('You can only upload JPG/PNG file!');
      setProfilePicError('You can only upload JPG/PNG file!');
      return Upload.LIST_IGNORE;
    }
    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isLt2M) {
      message.error('Image must smaller than 2MB!');
      setProfilePicError('Image must smaller than 2MB!');
      return Upload.LIST_IGNORE;
    }
    setProfilePicError(null); // Clear error on valid file
    return true;
  }, []);

  const handleImageChange = useCallback(({ fileList }) => {
    // This handler can be used to update the form's value directly if needed,
    // though `valuePropName="fileList"` and `getValueFromEvent` usually handle it.
    // If you need more complex state management related to fileList, you can do it here.
    // For now, we rely on Form.Item to manage the value based on valuePropName/getValueFromEvent.
    // Ensure imageUrl state is updated if the fileList changes (e.g., file removed)
    if (fileList.length === 0) {
      setImageUrl(null);
      setProfilePicError(null); // Clear error when no image
      setProfilePictureFile(null);
    } else if (fileList[0].status === 'done' && fileList[0].url) {
      setImageUrl(fileList[0].url);
      setProfilePictureFile(fileList[0])
      setProfilePicError(null);
    }
  }, []);

  const validateProfilePicture = useCallback((rule, value) => {
    // If the field is empty, and it's optional, it's valid.
    if (!value || value.length === 0) {
      return Promise.resolve();
    }

    const file = value[0]; // Assuming maxCount={1}

    // Check if the file was uploaded and has a 'done' status or valid URL/originFileObj
    if (file.status === 'done' || file.url || file.originFileObj) {
      // If there was an error from beforeUpload, reject
      if (profilePicError) {
        return Promise.reject(new Error(profilePicError));
      }
      return Promise.resolve(); // It's valid if done or has data
    }

    // If status is 'error', it means the upload failed previously
    if (file.status === 'error') {
      return Promise.reject(new Error('File upload failed. Please try again.'));
    }

    // If it's still uploading, resolve for now (validation will re-trigger on status change)
    if (file.status === 'uploading') {
      return Promise.resolve();
    }

    // Default rejection for any other invalid state
    return Promise.reject(new Error('Please upload a valid profile picture.'));
  }, [profilePicError]); // Dependency on profilePicError to re-run validation if error state changes

  // Cancel Confirmation Logic
  const showCancelConfirm = () => {
    confirm({

      title: 'Are you sure you want to cancel?',
      icon: <ExclamationCircleOutlined />,
      content: 'Any unsaved changes will be lost. This action cannot be undone.',
      okText: 'Yes, Cancel',
      okType: 'danger', // Make the "Yes, Cancel" button red
      cancelText: 'No, Keep Editing',
      onOk() {
        form.resetFields(); // Reset form fields to initial values (set by useEffect)
        setImageUrl(null);
        setProfilePictureFile(null);
        setProfilePicError(null);
        setProvinces(null);
        setDistricts(null);
        onCancel(); // Call the parent's onCancel to change view
      },
      onCancel() {
        // This code runs if the user clicks "No, Keep Editing" or closes the modal
      },
    });
  };

  const findLookupObject = (searchValue, lookupArray) => {
    if (!searchValue || !lookupArray || !Array.isArray(lookupArray)) {
      return null;
    }

    // Ensure comparison is consistent (e.g., trim spaces, convert to lowercase if your lookup is case-insensitive)
    const normalizedSearchValue = typeof searchValue === 'string' ? searchValue.trim().toLowerCase() : searchValue;

    return lookupArray.find(item => {
      const itemValue = typeof item.value === 'string' ? item.value.trim().toLowerCase() : item.value;
      const itemKey = typeof item.key === 'string' ? item.key.trim().toLowerCase() : item.key;

      return itemValue === normalizedSearchValue || itemKey === normalizedSearchValue;
    });
  };

  const removeProfilePicture = () => {
    setImageUrl(null);
    setProfilePictureFile(null);
    setProfilePicError(null);
    form.setFieldsValue({ profilePicture: [] });
    form.validateFields(['profilePicture']);
    message.info('Profile picture removed.');
  }

  const onFinish = async (values) => {

    let userData = { ...values };

    // --- Profile Picture processing for API ---
    if (userData.profilePicture && userData.profilePicture.length > 0) {
      const uploadedFile = userData.profilePicture[0];
      let finalBase64 = null;

      if (uploadedFile.url) {
        finalBase64 = uploadedFile.url;
      } else if (uploadedFile.originFileObj) {
        const reader = new FileReader();
        reader.readAsDataURL(uploadedFile.originFileObj);
        await new Promise(resolve => {
          reader.onload = () => {
            finalBase64 = reader.result;
            resolve();
          };
        });
      }

      if (finalBase64) {
        userData.profilePicture = {
          uid: uploadedFile.uid || dayjs().valueOf(),
          name: uploadedFile.name || 'profile_picture.png',
          size: uploadedFile.size || 0,
          type: uploadedFile.type || 'image/png',
          base64: finalBase64,
        };
      } else {
        userData.profilePicture = null;
        console.warn("No base64 data found for profile picture, setting to null.");
      }
    } else {
      userData.profilePicture = null;
    }

    // Construct the final data object for the API, ENSURING keys are sent for lookup fields.
    const data = {
      id: initialValues.id,
      dateOfBirth: userData.dateOfBirth ? userData.dateOfBirth : null,
      contactNumber: userData.contactNumber,
      gender: findLookupObject(userData.gender, gender)?.key || null,
      disability: findLookupObject(userData.disability, disability)?.key || null,
      languages: Array.isArray(userData.languages)
        // 1. userData.languages is now an array of values (e.g., ['English', 'Zulu'])
        // 2. Find the corresponding object in the 'languages' lookup array.
        // 3. Extract the 'key' (the ID) needed for the backend.
        ? userData.languages.map(languageValue => {
          const foundLang = languages?.find(lang => lang.value === languageValue);
          return parseInt(foundLang?.key);
        }).filter(Boolean) // Filter out any entries where the key couldn't be found or is NaN
        : [],
      preferEmail: userData.preferEmail ?? false,
      preferPhone: userData.preferPhone ?? false,
      location: (() => {
        const locationData = {};
        const selectedProvinceValue = userData.location?.province;
        const selectedDistrictValue = userData.location?.district;
        const selectedTownCityValue = userData.location?.town;

        const foundProvince = findLookupObject(selectedProvinceValue, provinces);
        if (foundProvince) {
          locationData.province = foundProvince.key;
          const foundDistrict = findLookupObject(selectedDistrictValue, foundProvince.districts || []);
          if (foundDistrict) {
            locationData.district = foundDistrict.key;
            const foundTownCity = findLookupObject(selectedTownCityValue, foundDistrict.towns || []);
            if (foundTownCity) {
              locationData.town = foundTownCity.key;
            }
          }
        }
        return locationData;
      })(),
      race: findLookupObject(userData.race, race)?.key || null,
      title: findLookupObject(userData.title, titles)?.key || null,
      volunteerDifferentLocation: userData.volunteerDifferentLocation,
    };
    const _data = { ...data }

    if (values.profilePicture.length === 0) {
      _data.profilePicture = [];
      onSave(_data)
      return
    }
    if ((values.profilePicture[0]?.name === initialValues?.profilePicture?.name)) {
      //Old image
      const base64 = `data:${initialValues.profilePicture?.type};base64,${initialValues.profilePicture?.base64}`
      const blob = util.base64ToBlob(base64);

      const originalFile = new File([blob], initialValues.profilePicture?.name, { type: blob.type });

      _data.profilePicture = originalFile;
      onSave(_data)
    } else {
      //Newly uploaded image
      data.profilePicture = values.profilePicture[0]?.originFileObj
      onSave(data);
    }
    setProfilePictureFile(null)
  };

  const onFinishFailed = (errorInfo) => {
    // message.error('Please fill in all required fields correctly.');

    if (errorInfo.errorFields.some(field => field.name[0] === 'profilePicture')) {
      setProfilePicError('Please upload a valid profile picture.');
    }
  };

  return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: '0px' }}>
      <Card
        title={<h2 className="card-head-title">PERSONAL INFORMATION</h2>}
        className="card-main"
        style={{ maxWidth: '800px', marginBottom: '40px' }}
      >
        <Form
          name={formName}
          form={form}
          // name="personal_information"
          layout="vertical"
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          initialValues={{
            preferEmail: false,
            preferPhone: false,
          }}

        >
          <div style={{ textAlign: 'center', marginBottom: '16px' }}>
            {/* Profile Picture Display Area */}
            <div style={{
              width: '100px', height: '100px', borderRadius: '50%',
              backgroundColor: '#f0f2f5', display: 'flex', justifyContent: 'center',
              alignItems: 'center', margin: '0 auto 15px auto', overflow: 'hidden',
            }}>
              {imageUrl ? (
                <img src={imageUrl} alt="profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                <UserOutlined style={{ fontSize: '40px', color: '#999' }} />
              )}
            </div>

            {/* Form Item for Upload with validation rules */}
            <Form.Item
              name="profilePicture"
              valuePropName="fileList"
              getValueFromEvent={e => (Array.isArray(e) ? e : e && e.fileList)}
              // noStyle
              rules={[{ validator: validateProfilePicture }]}
              // No label here, as the visual button serves as the primary interaction
              noStyle // Important to prevent default Form.Item styling that might affect button layout
            >
              <Space>
                {/* The actual Upload component, hidden, triggered by the visible Button */}
                <Upload
                  ref={uploadRef} // Assign ref to the Upload component
                  accept="image/*"
                  // name="avatar"
                  listType="text" // Use 'text' type for an invisible upload control
                  showUploadList={false} // Crucial to hide Antd's default upload list
                  // action="https://www.mocky.io/v2/5cc8019d300000980a055e76" // Replace with your actual upload URL
                  customRequest={customRequest} // <--- Use customRequest here
                  beforeUpload={beforeImageUpload}
                  onChange={handleImageChange}
                  maxCount={1}
                >
                  {/* This inner content of Upload is where the file input lives. It will be hidden */}
                  <div style={{ display: 'none' }}></div>
                </Upload>

                {/*Enable the below code to enable selfie button */}
                {/* <Button
                icon={<CameraOutlined />}
                // style={{ marginTop: '10px' }}
                onClick={handleTakeSelfieClick}
              >
                Take Selfie
              </Button> */}
                {/* </Form.Item> */}

                {/* Visible Upload Button */}
                <Button
                  type="dashed"
                  icon={<UploadOutlined />}
                  onClick={() => uploadRef.current.upload.uploader.fileInput.click()}
                  style={{
                    width: '240px',
                    borderColor: profilePicError ? '#ff4d4f' : 'red',
                    color: profilePicError ? '#ff4d4f' : 'red',
                  }}
                  loading={loading}
                >
                  Upload Profile Picture (optional)
                </Button>
              </Space>
            </Form.Item>
            {/* Custom Error Message Display for Profile Picture */}
            {profilePicError && (
              <div style={{ color: '#ff4d4f', fontSize: '14px', marginTop: '8px' }}>
                {profilePicError}
              </div>
            )}

            {imageUrl && (
              <div style={{
                display: 'flex', // Use flex to center the button
                justifyContent: 'center', // Center horizontally
                width: '100%', // Take full width to ensure centering
                marginTop: profilePicError ? '8px' : '16px', // Adjust margin based on error presence
              }}>
                <Button
                  type="default"
                  onClick={removeProfilePicture}
                >
                  Remove
                </Button>
              </div>
            )}
          </div>

          {/* Form Fields */}
          <Row gutter={[20, 20]}>
            <Col xs={24} sm={12} md={8}>
              <Form.Item
                label={<Text strong>Title</Text>}
                name="title"
                rules={[{ required: true, message: 'Please select your title' }]}
              >
                <Select >
                  {Array.isArray(titles) && titles.map(option => (
                    <Option key={option.key} value={option.value}>{option.value}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Form.Item
                label={<Text strong>First Name</Text>}
                name="firstName"
                rules={[{ required: true, message: 'Please enter your first name' }]}
              >
                <Input name="firstName" placeholder="Your first name" disabled={true} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Form.Item
                label={<Text strong>Last Name</Text>}
                name="lastName"
                rules={[{ required: true, message: 'Please enter your last name' }]}
              >
                <Input name="lastName" placeholder="Your last name" disabled={true} />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={8}>
              <Form.Item
                label="Contact Number"
                name="contactNumber"
                rules={[{
                  required: true,
                  validator: async (_, value) => {
                    // 'value' here is the combined string from ContactField
                    if (!value || value.trim() === '' || value.endsWith(' ')) {
                      throw new Error('Please input your contact number!');
                    }
                    // You can add more specific validation for the number part here
                    // e.g., if (!/\d{6,}/.test(value.split(' ')[1])) { ... }
                  },
                }]}
              >
                {/* Use your custom component */}
                <CustomContactNumberInput />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Form.Item
                label={<Text strong>Email</Text>}
                name="email"
                rules={[
                  { required: true, message: 'Please enter your email' },
                  { type: 'email', message: 'Please enter a valid email address' }
                ]}
              >
                <Input name="email" placeholder="janeDoe@gmail.com" disabled={true} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Tooltip title="Please enter your date of birth">
                <Form.Item
                  label="Date of Birth"
                  name="dateOfBirth"
                  rules={[
                    {
                      required: true,
                      message: 'Please enter your date of birth.',
                    },
                    () => ({
                      validator(_, value) {
                        if (!value) {
                          return Promise.resolve();
                        }

                        // Regex to check for YYYY-MM-DD format
                        const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
                        if (!dateRegex.test(value)) {
                          return Promise.reject(new Error('Invalid format. Please use YYYY-MM-DD.'));
                        }

                        const parsedDate = dayjs(value, 'YYYY-MM-DD', true); // 'true' enables strict parsing
                        if (!parsedDate.isValid()) {
                          return Promise.reject(new Error('Invalid date. Please enter a valid date.'));
                        }

                        // Check if the user is at least 18 years old
                        const eighteenYearsAgo = dayjs().subtract(18, 'year');
                        if (parsedDate.isAfter(eighteenYearsAgo, 'day')) {
                          return Promise.reject(new Error('You must be at least 18 years old.'));
                        }

                        return Promise.resolve();
                      },
                    }),
                  ]}

                >
                  <Input
                    name="dateOfBirth"
                    placeholder="YYYY-MM-DD"
                    onChange={(e) => {
                      let value = e.target.value.replace(/\D/g, ""); // only digits

                      if (value.length > 4 && value.length <= 6) {
                        value = value.slice(0, 4) + "-" + value.slice(4);
                      } else if (value.length > 6) {
                        value = value.slice(0, 4) + "-" + value.slice(4, 6) + "-" + value.slice(6, 8);
                      }

                      form.setFieldsValue({ dateOfBirth: value });
                    }}
                  />
                </Form.Item>
              </Tooltip>
            </Col>

            <Col xs={24} sm={12} md={8}>
              <Form.Item
                label={<Text strong>Race</Text>}
                name="race"
                rules={[{ required: true, message: 'Please select your race' }]}
              >
                <Select style={{ width: '100%' }} placeholder="Select Race">
                  {race.map((option) => (
                    <Option key={option.key} value={option.value}>{option.value}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Form.Item
                label={<Text strong>Gender</Text>}
                name="gender"
                rules={[{ required: true, message: 'Please select your gender' }]}
              >
                <Select style={{ width: '100%' }} placeholder="Select Gender">
                  {Array.isArray(gender) && gender.map(option => (
                    <Option key={option.key} value={option.value}>{option.value}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Form.Item
                label={<Text strong>Disability</Text>}
                name="disability"
                rules={[{ required: true, message: 'Please indicate if you have a disability' }]}
              >
                <Select style={{ width: '100%' }} placeholder="Select Option">
                  {Array.isArray(disability) && disability.map(option => (
                    <Option key={option.key} value={option.value}>{option.value}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          {/* Section: Language(s) */}
          <div style={{ marginTop: '20px', marginBottom: '20px' }}>
            <Form.Item name="languages" label="Select the language(s) you can speak" rules={[{ required: true, message: 'Please select at least one language' }]}>
              <Checkbox.Group
                onChange={onChangeLanguage}
                options={languages}
                value={selectedItems}
                style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}
              />

            </Form.Item>
          </div>

          <Col xs={24} sm={24} md={24}>
            <Form.Item
              label={<Text strong>Preferred method of communication</Text>}
              required
              name="preferredCommunication"
              rules={[
                {
                  validator: (_, __) => {
                    const emailChecked = form.getFieldValue('preferEmail');
                    const phoneChecked = form.getFieldValue('preferPhone');
                    if (emailChecked || phoneChecked) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('Please select at least one method of communication'));
                  },
                },
              ]}
            >
              <div style={{ display: 'flex', gap: '20px', marginTop: '8px' }}>
                <Form.Item name="preferEmail" valuePropName="checked" style={{ marginBottom: 0 }}>
                  <Checkbox>Email</Checkbox>
                </Form.Item>
                <Form.Item name="preferPhone" valuePropName="checked" style={{ marginBottom: 0 }}>
                  <Checkbox>Phone</Checkbox>
                </Form.Item>
              </div>
            </Form.Item>
          </Col>

          {/* Location Information */}
          <div style={{ border: '1px solid #d9d9d9', padding: '16px', borderRadius: '8px', marginBottom: '24px' }}>
            <Title level={5} style={{ marginTop: '0', marginBottom: '16px' }}>Volunteer Location</Title>
            <Form.Item style={{ marginBottom: 0 }} name="location">
              <Row gutter={16}>
                <Col xs={24} sm={8}>
                  <Form.Item
                    label={<Text strong>Province</Text>}
                    name={['location', 'province']}
                    rules={[{ required: true, message: 'Please select your province' }]}
                  >
                    <Select
                      placeholder="Select Province"
                      allowClear
                      onChange={handleProvinceChange}
                    >
                      {provinces.map(province => (
                        <Option key={province.key} value={province.value}>{province.value}</Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={8}>
                  <Form.Item
                    label={<Text strong>District</Text>}
                    name={['location', 'district']}
                    rules={[{ required: true, message: 'Please select your district!' }]}
                  >
                    <Select
                      placeholder="Select District"
                      allowClear
                      onChange={handleDistrictChange}
                    >
                      {districts.map(district => (
                        <Option key={district.key} value={district.value}>{district.value}</Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={8}>
                  <Form.Item
                    label={<Text strong>Nearest Town/City</Text>}
                    name={['location', 'town']}
                    rules={[{ required: true, message: 'Please select your nearest town/city!' }]}
                  >
                    <Select
                      placeholder="Select Nearest Town/City"
                      allowClear
                    >
                      {towns.map(town => (
                        <Option key={town.key} value={town.value}>{town.value}</Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
            </Form.Item>
            <Col xs={24} sm={12}>
              <Form.Item
                label={<Text strong>Are you open to volunteer outside your location?</Text>}
                name={'volunteerDifferentLocation'}
                rules={[{ required: true }]}
              >
                <Radio.Group>
                  <Space>
                    <Radio value={true}>Yes</Radio>
                    <Radio value={false}>No</Radio>
                  </Space>
                </Radio.Group>
              </Form.Item>
            </Col>
          </div>
          <Form.Item style={{ textAlign: 'right', marginTop: '24px', marginBottom: '0px' }}>
            <Button onClick={showCancelConfirm} style={{ marginRight: '8px' }} className="secondary-volunteer-button">
              Cancel
            </Button>
            <Button type="primary" htmlType="submit" className="primary-volunteer-button">
              Save
            </Button>
          </Form.Item>
        </Form>
      </Card>
      {/* Camera Modal */}
      <Modal
        title="Take a Selfie"
        visible={cameraModalVisible}
        onCancel={() => {
          setCameraModalVisible(false);
          stopCamera();
        }}
        footer={[
          <Button key="back" onClick={() => {
            setCameraModalVisible(false);
            stopCamera();
          }}>
            Cancel
          </Button>,
          <Button key="submit" type="primary" onClick={handleTakePhoto}>
            Take Photo
          </Button>,
        ]}
      >
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <video ref={videoRef} autoPlay playsInline style={{ width: '100%', maxWidth: '300px', borderRadius: '8px' }}></video>
          <canvas ref={canvasRef} style={{ display: 'none' }}></canvas>
        </div>
      </Modal>
    </div>
  );
}
